/**
 * HeroBlock — slim greeting + commission + wallet strip.
 *
 * Single role: surface "where am I today" in one card. Detail visualisations
 * live in MainChart below; KPIs in the row above the chart. No cinematic
 * effects, no inline curve duplication.
 */
import { ArrowDownRight, ArrowUpRight, Plus, Send } from 'lucide-react';
import { Button, Card, CardContent, Tooltip } from '@/components/ui';
import { formatCents, formatNumber } from '@/lib/format';
import type { DashboardMock, PartnerState } from '@/mocks/dashboard';

function plural(n: number, [one, few, many]: [string, string, string]): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}
function deltaPct(curr: number, prev: number): number | null {
  if (!Number.isFinite(prev) || prev <= 0) return curr > 0 ? 100 : null;
  const d = ((curr - prev) / prev) * 100;
  return Number.isFinite(d) ? d : null;
}
function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return trimmed[0]?.toUpperCase() ?? '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function HeroBlock({
  state,
  data,
  displayName,
  wallet,
}: {
  state: PartnerState;
  data: DashboardMock['hero'];
  displayName: string;
  wallet: DashboardMock['wallet'];
}) {
  const hour = new Date().getHours();
  const timeWord = hour < 6 ? 'ночи' : hour < 12 ? 'утро' : hour < 18 ? 'день' : 'вечер';
  const greetingPrefix = hour < 6 ? 'Доброй' : 'Добрый';

  // ── ZERO state — clean empty card with single CTA pointing to /offers ──
  if (state === 'zero') {
    return (
      <Card>
        <CardContent className="!p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <span
              className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-[var(--accent-soft)] text-[var(--accent-bright)] text-[14px] font-semibold tabular-nums shrink-0"
              aria-hidden
            >
              {initials(displayName)}
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-[24px] md:text-[28px] font-semibold tracking-tight text-[var(--text)] leading-tight m-0">
                {greetingPrefix} {timeWord}, {displayName}
              </h2>
              <p className="mt-2 text-[13px] text-[var(--text-secondary)] max-w-[560px]">
                Готов запустить первый трафик? Выбери оффер, создай трекинг-ссылку, размести её — конверсии засчитываются автоматически.
              </p>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <Button variant="gradient" size="lg" onClick={() => { window.location.hash = '/offers'; }}>
                  <Plus size={15} strokeWidth={2.5} />
                  Выбрать оффер
                </Button>
                <Button variant="ghost" size="lg" onClick={() => { window.location.hash = '/links'; }}>
                  К ссылкам
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isWarming = state === 'warming';
  const delta = deltaPct(data.commissionCents, data.commissionCentsPrev);
  const canWithdraw = wallet.availableCents >= wallet.minPayoutCents;
  const diff = Math.max(0, wallet.minPayoutCents - wallet.availableCents);

  return (
    <Card>
      <CardContent className="!p-6">
        {/* ── Top row: avatar + greeting ───────────────────────────────── */}
        <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-[var(--accent-soft)] text-[var(--accent-bright)] text-[14px] font-semibold tabular-nums shrink-0"
              aria-hidden
            >
              {initials(displayName)}
            </span>
            <div className="min-w-0">
              <div className="text-[18px] font-semibold tracking-tight text-[var(--text)] truncate leading-tight">
                {greetingPrefix} {timeWord}, {displayName}
              </div>
              <div className="text-[12px] text-[var(--text-muted)] tabular-nums mt-0.5">
                Сегодня · с 00:00 МСК
                {data.refreshedAtSec != null && ` · обновлено ${data.refreshedAtSec} сек назад`}
              </div>
            </div>
          </div>
        </div>

        {/* ── Big number row ───────────────────────────────────────────── */}
        {isWarming ? (
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-[44px] md:text-[52px] leading-none font-semibold tabular-nums text-[var(--text)]" style={{ letterSpacing: 'var(--tracking-tight)' }}>
              {formatNumber(data.clicks)}
            </span>
            <span className="text-[15px] text-[var(--text-muted)]">
              {plural(data.clicks, ['клик', 'клика', 'кликов'])} сегодня
            </span>
          </div>
        ) : (
          <div className="flex items-baseline gap-4 flex-wrap">
            <span
              className="text-[44px] md:text-[56px] leading-none font-semibold tabular-nums text-[var(--text)]"
              style={{ letterSpacing: 'var(--tracking-tight)' }}
            >
              {formatCents(data.commissionCents)}
            </span>
            {delta != null && (
              <Tooltip content={`${delta >= 0 ? '+' : '−'}${formatCents(Math.abs(data.commissionCents - data.commissionCentsPrev))} vs предыдущий период`}>
                <span
                  className="inline-flex items-center gap-0.5 text-[14px] font-semibold tabular-nums cursor-help"
                  style={{ color: delta >= 0 ? 'var(--success)' : 'var(--danger)' }}
                >
                  {delta >= 0 ? <ArrowUpRight size={15} strokeWidth={2.5} /> : <ArrowDownRight size={15} strokeWidth={2.5} />}
                  {Math.abs(delta).toFixed(1)}%
                </span>
              </Tooltip>
            )}
            <span className="text-[12px] text-[var(--text-quiet)] tabular-nums">
              vs {formatCents(data.commissionCentsPrev)}
            </span>
          </div>
        )}

        {isWarming && (
          <p className="text-[13px] text-[var(--text-secondary)] mt-3 max-w-[560px]">
            Трафик идёт. Первая конверсия обычно появляется через 2–6 часов после старта. Если кликов много, а конверсий нет — проверь, что ссылка ведёт на правильный лендинг.
          </p>
        )}

        {/* ── Wallet footer row ────────────────────────────────────────── */}
        <div className="mt-6 pt-4 border-t border-[var(--border-subtle)] flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-5 flex-wrap min-w-0">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                Доступно
              </span>
              <span
                className="text-[20px] font-semibold tabular-nums text-[var(--text)] leading-none"
                style={{ letterSpacing: 'var(--tracking-tight)' }}
              >
                {formatCents(wallet.availableCents)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 text-[12px]">
              <span className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                В hold
              </span>
              <span className="tabular-nums text-[var(--warning)] font-semibold leading-none">
                {formatCents(wallet.holdCents)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 text-[12px]">
              <span className="text-[10px] uppercase tracking-[var(--tracking-eyebrow)] font-semibold text-[var(--text-muted)]">
                На модерации
              </span>
              <span className="tabular-nums text-[var(--text-muted)] font-semibold leading-none">
                {formatCents(wallet.pendingCents)}
              </span>
            </div>
          </div>
          {canWithdraw ? (
            <Button variant="gradient" size="sm" onClick={() => { window.location.hash = '/payouts?create=1'; }}>
              <Send size={13} strokeWidth={2.5} />
              Запросить выплату
            </Button>
          ) : (
            <Tooltip content={`Минимум к выплате — ${formatCents(wallet.minPayoutCents)}`}>
              <Button variant="secondary" size="sm" disabled>
                <Send size={13} strokeWidth={2.5} />
                До минимума {formatCents(diff)}
              </Button>
            </Tooltip>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
